// ============================================================
// ENGINE V2 — MUTATION ENGINE
// ============================================================
// Runs after a student completes an activity (game, quiz,
// test, micro lesson). Two responsibilities:
//
//   1. Apply the diffing algorithm to update career path
//      trait scores for affected active nodes.
//
//   2. Recompute the student's general traits by averaging
//      all similar traits across all active career path nodes.
//
// The diffing algorithm formula (agreed on 2025):
//
//   diff     = |activityScore − current|
//   diffPct  = diff / SCALE_MAX
//   delta    = diffPct × current          (self-dampening)
//   rawNew   = current ± delta            (+ if well, − if poor)
//   smoothed = (n × current × R + rawNew) / (n × R + 1)
//   result   = max(FLOOR, min(CEILING, smoothed))
//
// where n = DAMPING (globally fixed), R = SMOOTHING_RATIO.
// ============================================================

import type {
  StudentProfile,
  ActiveNode,
  CareerPathTrait,
  GeneralTrait,
  ActivityResult,
  ActivityTrait,
  MutationResult,
} from './types';
import {
  DAMPING,
  SMOOTHING_RATIO,
  SCALE_MAX,
  SCORE_FLOOR,
  SCORE_CEILING,
} from './constants';

// ─── DIFFING ALGORITHM ───────────────────────────────────────

function diffTraitScore(
  current:       number,
  activityScore: number,
  damping:       number,
  performedWell: boolean
): number {
  // Step 1: Raw distance between activity score and current score
  const diff = Math.abs(activityScore - current);

  // Step 2: Express diff as a fraction of the full scale
  const diffPct = diff / SCALE_MAX;

  // Step 3: Delta is a percentage of the current score
  // (self-dampening: high scores resist change more than low scores)
  const delta = diffPct * current;

  // Step 4: Move score up or down symmetrically
  const rawNew = performedWell ? current + delta : current - delta;

  // Step 5: Quartile smoothing with normalised denominator
  // This prevents a single activity from making large jumps.
  const smoothed =
    (damping * current * SMOOTHING_RATIO + rawNew) /
    (damping * SMOOTHING_RATIO + 1);

  // Step 6: Enforce floor AFTER smoothing, then clamp ceiling
  return Math.min(SCORE_CEILING, Math.max(SCORE_FLOOR, smoothed));
}

// A student performed well on a trait if their activity score
// for that trait exceeds their current score for it.
// (They demonstrated above their current standing.)
function didPerformWell(activityScore: number, currentScore: number): boolean {
  return activityScore > currentScore;
}

// ─── NODE TRAIT MUTATION ──────────────────────────────────────

// Mutate the traits for a single active node based on the
// activity's trait results. Only traits that appear in both
// the node and the activity results are affected.

function mutateNodeTraits(
  nodeTraits: CareerPathTrait[],
  activityTraits: ActivityTrait[],
  traitsAffectedAccumulator: Set<string>
): CareerPathTrait[] {
  return nodeTraits.map(nodeTrait => {
    const activityTrait = activityTraits.find(
      a => a.traitId === nodeTrait.traitId
    );

    // No matching activity trait — leave this trait unchanged
    if (!activityTrait) return nodeTrait;

    const performedWell = didPerformWell(activityTrait.score, nodeTrait.score);
    const newScore = diffTraitScore(
      nodeTrait.score,
      activityTrait.score,
      DAMPING,
      performedWell
    );

    traitsAffectedAccumulator.add(nodeTrait.name);

    return { ...nodeTrait, score: newScore };
  });
}

// ─── DETERMINE WHICH NODES TO UPDATE ─────────────────────────
// If the activity is tied to a specific career path node:
//   Only that node is mutated.
// If the activity has no specific career path (null):
//   All active nodes that share any of the activity's traits
//   are mutated. This handles general activities (e.g. a
//   general quiz that touches traits spanning multiple paths).

function shouldMutateNode(
  node: ActiveNode,
  activity: ActivityResult
): boolean {
  if (node.status !== 'active') return false;

  // Activity targets a specific node
  if (activity.careerPathNodeId !== null) {
    return node.nodeId === activity.careerPathNodeId;
  }

  // General activity — mutate any active node that shares a trait
  const activityTraitIds = new Set(activity.traitResults.map(t => t.traitId));
  return node.traits.some(t => activityTraitIds.has(t.traitId));
}

// ─── GENERAL TRAIT RECOMPUTATION ─────────────────────────────
// After career path traits are updated, recompute general traits
// by averaging the scores of matching traits across all active nodes.
//
// If a trait appears in multiple active nodes, the scores are
// averaged. This aggregate becomes the student's general score
// for that trait.

function recomputeGeneralTraits(
  currentGeneralTraits: GeneralTrait[],
  updatedActiveNodes: ActiveNode[]
): GeneralTrait[] {
  // Accumulate scores per trait across all active nodes
  const accumulator = new Map<string, { name: string; scores: number[] }>();

  for (const node of updatedActiveNodes) {
    if (node.status !== 'active') continue;

    for (const trait of node.traits) {
      const existing = accumulator.get(trait.traitId);
      if (existing) {
        existing.scores.push(trait.score);
      } else {
        accumulator.set(trait.traitId, {
          name: trait.name,
          scores: [trait.score],
        });
      }
    }
  }

  // Build updated general traits list
  const updated: GeneralTrait[] = [...currentGeneralTraits];

  for (const [traitId, { name, scores }] of accumulator) {
    const average = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    const existingIndex = updated.findIndex(t => t.traitId === traitId);

    if (existingIndex >= 0) {
      // Trait exists — update score and increment update counter
      updated[existingIndex] = {
        ...updated[existingIndex],
        score: average,
        updateCount: updated[existingIndex].updateCount + 1,
      };
    } else {
      // New trait encountered — add it to the general profile
      updated.push({
        traitId,
        name,
        score: average,
        updateCount: 1,
      });
    }
  }

  return updated;
}

// ─── MUTATION ENGINE ─────────────────────────────────────────

export function runMutationEngine(
  student: StudentProfile,
  activity: ActivityResult
): MutationResult {
  const traitsAffected = new Set<string>();

  // 1. Update trait scores on all qualifying active nodes
  const updatedActiveNodes: ActiveNode[] = student.activeNodes.map(node => {
    if (!shouldMutateNode(node, activity)) return node;

    const mutatedTraits = mutateNodeTraits(
      node.traits,
      activity.traitResults,
      traitsAffected
    );

    return {
      ...node,
      traits: mutatedTraits,
      lastUpdatedAt: new Date(),
    };
  });

  // 2. Recompute general traits from the updated career path traits
  const updatedGeneralTraits = recomputeGeneralTraits(
    student.generalTraits,
    updatedActiveNodes
  );

  return {
    studentId: student.studentId,
    updatedActiveNodes,
    updatedGeneralTraits,
    traitsAffected: Array.from(traitsAffected),
  };
}
